import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { getMobileOS } from '../../utils/mobileOS';
import { useOpenAppBannerDismissed } from './useOpenAppBannerDismissed';
import { useOpenAppBannerStyles } from './style';
import { buildShimUrl } from './constants';
import DataProvider from '../../provider';

const mobileOS = getMobileOS();

/**
 * Banner shown on the home page and on a selling area, prompting mobile browsers
 * to open (or install) the native app.
 *
 * Visibility is driven by the `showOpenAppBanner` flag on the Place behind the
 * selling area. Pass `sellingPointImpulseUuid` when the caller already knows which
 * selling area is on screen — otherwise the API falls back to the user's last
 * visited one, which is written fire-and-forget and may still be stale.
 */
export default function OpenAppBanner({style, sellingPointImpulseUuid}: {style?: CSSProperties, sellingPointImpulseUuid?: string}) {
	const classes = useOpenAppBannerStyles();
	const { isDismissed, dismiss } = useOpenAppBannerDismissed();
  const [visible, setVisible] = useState(false);
  const dataProvider = useMemo(() => DataProvider(process.env.REACT_APP_WAREHOUSE_API || ''), []);
	// Which selling area we already answered for. Navigating between two selling
	// areas keeps this component mounted (only the :id route param changes), so
	// without this we'd keep showing the previous area's answer.
	const answeredFor = useRef<string | null>(null);

	useEffect(() => {
		// Nothing to show on desktop or once dismissed — don't spend a request on it.
		if (!mobileOS || isDismissed) {
			return;
		}

		const key = sellingPointImpulseUuid || '';
		if (answeredFor.current === key) {
			return;
		}
		answeredFor.current = key;
		// Hide while re-answering, so a new selling area never inherits the
		// previous one's banner for the duration of the request.
		setVisible(false);

		// Guards against a slow response for the previous selling area landing after
		// the current one and overwriting it (and against setState on unmount).
		let cancelled = false;
		const query = sellingPointImpulseUuid ? `?sellingPointImpulseUuid=${encodeURIComponent(sellingPointImpulseUuid)}` : '';

		dataProvider(`b2c/show-open-app-banner${query}`)
			.then(data => {
				if (!cancelled) {
					setVisible(!!data?.showOpenAppBanner);
				}
			})
			.catch(e => {
				if (!cancelled) {
					setVisible(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [dataProvider, isDismissed, sellingPointImpulseUuid]);

	if (!mobileOS || isDismissed || !visible) {
		return null;
	}

	return (
		<div className={classes.root} style={style}>
			<IconButton className={classes.closeButton} aria-label="Chiudi banner" size="small" onClick={dismiss}>
				<CloseIcon fontSize="small" />
			</IconButton>
			{/*
				A plain <a> — not a MUI button — because iOS only treats a genuine anchor
				tap as a Universal Link. Must not preventDefault or navigate via JS, or
				the OS stops intercepting and the app never opens.
			*/}
			<a
				className={classes.link}
				href={buildShimUrl()}
				target="_self"
				aria-label="Apri o scarica l'app Foorban"
			>
				<Typography variant="body2" className={classes.messageText}>
				  Scarica la nuova app di Foorban
				</Typography>
				<ArrowForwardIcon className={classes.openIcon} />
			</a>
		</div>
	);
}
